import { useMemo } from "react";
import Editor from "react-simple-code-editor";
import "./styles/main.scss";
import { StageElement, Component } from "../../../types";
import { hljs } from "./codeBlockHljs";
import {
  convertCodeBlockContentToString,
  postProcessLineHighlightedHtml,
  prepareCodeForLineHighlight,
} from "./codeBlockStringUtils";

const defaultTheme: React.CSSProperties = {
  "--theme-text-color": "rgba(0, 0, 0, 0.7)",
  "--theme-title-font": "system-ui",
} as React.CSSProperties;

type CodeBlockPluginConfig = {
  codeBlockText?: unknown;
  showLineNumber?: string;
  lineNumber?: number | string;
  language?: string;
  coloring?: string;
  appearance?: string;
  activityLabel?: string;
  activityTitle?: string;
  instructions?: string;
  captions?: string;
  cssClass?: string;
};

function parsePluginConfig(raw: unknown): CodeBlockPluginConfig {
  if (raw == null) return {};
  if (typeof raw === "string") {
    try {
      return JSON.parse(raw) as CodeBlockPluginConfig;
    } catch {
      return {};
    }
  }
  return raw as CodeBlockPluginConfig;
}

function Gutter(props: {
  totalLines: number;
  startLine: number;
  showLines: boolean;
  bgColor: string;
}) {
  const { totalLines, startLine = 1, showLines = true, bgColor } = props;
  const items = Array.from({ length: totalLines }, (_, i) => (
    <div className="gutterElement" key={i}>
      {i + startLine}
    </div>
  ));
  return (
    <div className="gutterContainer" aria-hidden="true" style={{ backgroundColor: bgColor }}>
      <pre>
        <code>{showLines && items}</code>
      </pre>
    </div>
  );
}

type CodeBlockPluginComponentProps = {
  element: StageElement;
  componentConfig: Component;
};

export function CodeBlockPluginComponent({ componentConfig }: CodeBlockPluginComponentProps) {
  const cfg = parsePluginConfig(componentConfig.config);
  const {
    codeBlockText,
    showLineNumber = "on",
    lineNumber = 1,
    language = "no language",
    coloring = "on",
    appearance = "light",
    activityLabel,
    activityTitle,
    instructions,
    captions,
    cssClass,
  } = cfg;

  const backgroundColor = appearance === "dark" ? "#F6F7F8" : "#fff";
  const startLine = parseInt(String(lineNumber), 10) || 1;
  const showLines = showLineNumber === "on";
  const codeAsString = convertCodeBlockContentToString(codeBlockText);
  const totalLineCount = Math.max(1, codeAsString.split("\n").length);

  const componentId = componentConfig.$id || "";
  const editorTextareaId = `code-block-editor-${componentId}`;

  const highlight = useMemo(() => {
    return (code: string) => {
      const lang = language === "no language" ? "plaintext" : language;
      const safeHighlight = (value: string): string => {
        try {
          return hljs.highlight(value, { language: lang }).value;
        } catch {
          try {
            return hljs.highlight(value, { language: "plaintext" }).value;
          } catch {
            return String(value).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
          }
        }
      };
      if (!showLines) {
        return safeHighlight(String(code));
      }
      const markedCode = prepareCodeForLineHighlight(code);
      const highlighted = safeHighlight(markedCode);
      return postProcessLineHighlightedHtml(highlighted, startLine);
    };
  }, [language, showLines, startLine]);

  const languageClass = language === "no language" ? "language-plaintext" : `language-${language}`;

  return (
    <div className={`code-block-plugin ${cssClass ?? ""}`.trim()}>
      <div className="layout" data-id={componentId}>
        <>
          {(activityTitle || activityLabel) && (
            <div className="component-label-container">
              <h4 className="component-label-section">
                {activityLabel && (
                  <span
                    data-id={`${componentId}-activityLabel`}
                    className="component-label"
                    dangerouslySetInnerHTML={{ __html: activityLabel }}
                  />
                )}
                {activityTitle && (
                  <span
                    data-id={`${componentId}-activityTitle`}
                    className="component-title"
                    dangerouslySetInnerHTML={{ __html: activityTitle }}
                  />
                )}
              </h4>
            </div>
          )}
          {instructions && (
            <div
              data-id={`${componentId}-instructions`}
              className="component-instructions"
              dangerouslySetInnerHTML={{ __html: instructions }}
            />
          )}
        </>

        <div className="component-container" style={defaultTheme}>
          <div className="codeEditorContainer">
            <Gutter
              totalLines={totalLineCount}
              startLine={startLine}
              showLines={showLines}
              bgColor={backgroundColor}
            />
            <div className="codeEditor" style={{ backgroundColor: backgroundColor }}>
              <pre>
                <code className={languageClass}>
                  {coloring === "on" ? (
                    <Editor
                      value={codeAsString}
                      onValueChange={() => {}}
                      highlight={highlight}
                      readOnly
                      textareaId={editorTextareaId}
                      className="npm__react-simple-code-editor__textarea"
                    />
                  ) : (
                    <span className="code-content">{codeAsString}</span>
                  )}
                </code>
              </pre>
            </div>
          </div>
        </div>

        {captions && (
          <div
            data-id={`${componentId}-captions`}
            className="component-caption"
            dangerouslySetInnerHTML={{ __html: captions }}
          />
        )}
      </div>
    </div>
  );
}
